import type { EntityDiscoveryItem, SelectedEntity } from '../../api/types';

export type PostgreSQLSyncStrategy = 'polling' | 'cdc';

export function inferPostgreSQLSyncStrategy(selections: Iterable<SelectedEntity>): PostgreSQLSyncStrategy {
  return Array.from(selections).some(selection => selection.sync_mode === 'cdc') ? 'cdc' : 'polling';
}

/** Apply one connection strategy without mutating selections or saved polling drafts. */
export function applyPostgreSQLSyncStrategy(
  selected: Map<string, SelectedEntity>,
  entities: EntityDiscoveryItem[],
  strategy: PostgreSQLSyncStrategy,
  pollingDrafts: ReadonlyMap<string, SelectedEntity> = new Map(),
): Map<string, SelectedEntity> {
  const byName = new Map(entities.map(entity => [entity.name, entity]));
  return new Map(Array.from(selected, ([name, selection]) => {
    const entity = byName.get(name);
    if (!entity?.polling) return [name, selection];
    if (strategy === 'cdc') return [name, selectPollingMode(entity, selection, 'cdc')];
    const polling = pollingDrafts.get(name);
    return [name, polling && polling.sync_mode !== 'cdc' ? { ...polling,
      identity_key_columns: polling.identity_key_columns?.slice() } : defaultEntitySelection(entity)];
  }));
}

/** Initialize new selections only; recommendations never acknowledge the source contract. */
export function defaultEntitySelection(entity: EntityDiscoveryItem): SelectedEntity {
  const preview = entity.polling;
  if (preview?.proposed_mode === 'incremental' && preview.sample_size > 0 && preview.watermark_column
      && Array.isArray(preview.key_columns)) {
    // The backend owns timestamp-name and sample validation; verify the recommended tuple is selectable.
    const recommended: SelectedEntity = {
      entity_name: entity.name,
      label: entity.label,
      sync_mode: 'incremental',
      identity_key_columns: [...preview.key_columns],
      incremental_field: preview.watermark_column,
      source_contract_acknowledged: false,
    };
    if (canSelectIncremental(entity, recommended)) return recommended;
  }
  const incremental = !entity.polling && entity.supports_incremental;
  return {
    entity_name: entity.name,
    label: entity.label,
    sync_mode: incremental ? 'incremental' : 'full',
    ...(incremental && entity.incremental_fields.length
      ? { incremental_field: entity.incremental_fields[0] } : {}),
  };
}

/** Discovery payloads can be stale or incomplete despite the generated API type. */
export function pollingTimestampColumns(preview: EntityDiscoveryItem['polling']) {
  const columns = preview?.timestamp_columns;
  return Array.isArray(columns) && columns.every(column => column && typeof column.name === 'string'
    && (column.reason === null || typeof column.reason === 'string')) ? columns : null;
}

// Operational timestamps are not row-change watermarks. Keep this list explicit:
// broad last_*/sync_* filters would also hide legitimate source business columns.
const OPERATIONAL_TIMESTAMP_COLUMNS = new Set([
  'last_materialized_at', 'last_tested_at', 'last_sync_at', 'sync_claimed_at',
  'stats_updated_at',
]);

export function selectablePollingTimestampColumns(preview: EntityDiscoveryItem['polling']) {
  const eligibleColumns = preview?.eligible_columns;
  if (!Array.isArray(eligibleColumns)) return null;
  return pollingTimestampColumns(preview)?.filter(column => column.reason === null
    && eligibleColumns.includes(column.name)
    && !column.name.startsWith('_')
    && !OPERATIONAL_TIMESTAMP_COLUMNS.has(column.name.toLowerCase())) ?? null;
}

export function needsPollingMetadataRefresh(entities: EntityDiscoveryItem[]): boolean {
  return entities.some(entity => !!entity.polling && (pollingTimestampColumns(entity.polling) === null
    || pollingIdentityKeys(entity.polling) === null || !Array.isArray(entity.polling.eligible_columns)
    || !Array.isArray(entity.polling.primary_key_columns)));
}

export function pollingIdentityKeys(preview: EntityDiscoveryItem['polling']) {
  const keys = preview?.identity_keys;
  return Array.isArray(keys) && keys.every(key => key && typeof key.name === 'string'
    && (key.type === 'p' || key.type === 'u') && Array.isArray(key.columns) && key.columns.length > 0
    && key.columns.every(column => typeof column === 'string')) ? keys : null;
}

export function selectedPollingIdentity(entity: EntityDiscoveryItem, selection: SelectedEntity): string[] {
  return selection.identity_key_columns ?? entity.polling?.key_columns ?? [];
}

export function canSelectIncremental(entity: EntityDiscoveryItem, selection: SelectedEntity): boolean {
  // Discovery suggestions are not selections, including when a saved choice is cleared.
  const field = selection.incremental_field;
  const identity = selectedPollingIdentity(entity, selection);
  const validTimestamp = Array.isArray(entity.polling?.primary_key_columns) && !!field
    && !!selectablePollingTimestampColumns(entity.polling)?.some(column => column.name === field);
  const validIdentity = !!identity.length && !!pollingIdentityKeys(entity.polling)?.some(
    key => key.columns.length === identity.length
      && key.columns.every((column, index) => column === identity[index]),
  );
  if (selection.incremental_behavior === 'append_only') {
    return validTimestamp && (identity.length === 0 || validIdentity);
  }
  return validTimestamp && validIdentity;
}

/** Keep unsaved choices, but a changed source contract requires a fresh opt-in. */
export function reconcilePollingSelections(
  selections: Map<string, SelectedEntity>, previous: EntityDiscoveryItem[], refreshed: EntityDiscoveryItem[],
): Map<string, SelectedEntity> {
  const before = new Map(previous.map(entity => [entity.name, entity]));
  const after = new Map(refreshed.map(entity => [entity.name, entity]));
  return new Map(Array.from(selections, ([name, selection]) => {
    const oldEntity = before.get(name);
    const entity = after.get(name);
    if (!oldEntity?.polling && !entity?.polling) return [name, selection];
    const contractChanged = !entity?.polling
      || !canEnablePolling(entity, { ...selection, source_contract_acknowledged: true })
      || JSON.stringify(oldEntity?.polling?.identity_keys) !== JSON.stringify(entity.polling.identity_keys)
      || JSON.stringify(oldEntity?.polling?.key_columns) !== JSON.stringify(entity.polling.key_columns);
    return [name, contractChanged
      ? { ...selection, sync_mode: 'full', source_contract_acknowledged: false }
      : selection];
  }));
}

export function canEnablePolling(entity: EntityDiscoveryItem, selection: SelectedEntity): boolean {
  return selection.source_contract_acknowledged === true && canSelectIncremental(entity, selection);
}

/** Apply dialog settings locally without mutating state or reselecting a removed table. */
export function applyEntitySelectionDraft(
  selected: Map<string, SelectedEntity>, draft: SelectedEntity,
): Map<string, SelectedEntity> {
  if (!selected.has(draft.entity_name)) return selected;
  const next = new Map(selected);
  next.set(draft.entity_name, { ...draft, identity_key_columns: draft.identity_key_columns?.slice() });
  return next;
}

/** Retain unavailable choices until the user explicitly removes them or refresh recovers them. */
export function unavailableEntitySelections(
  selected: Map<string, SelectedEntity>, entities: EntityDiscoveryItem[],
): SelectedEntity[] {
  const available = new Set(entities.filter(entity => entity.is_queryable).map(entity => entity.name));
  return Array.from(selected.values()).filter(selection => !available.has(selection.entity_name));
}

/** Collect every row issue in display order; use the same rules for rendering and saving. */
export function entitySelectionIssues(selected: Map<string, SelectedEntity>, entities: EntityDiscoveryItem[]): Map<string, string> {
  const issues = new Map<string, string>();
  for (const selection of unavailableEntitySelections(selected, entities)) {
    issues.set(selection.entity_name, 'Unavailable in current discovery. Remove this selection or refresh table details before saving.');
  }
  for (const entity of entities) {
    const selection = selected.get(entity.name);
    if (!entity.is_queryable || !entity.polling || !selection) continue;
    if (selection.sync_mode === 'cdc') {
      if (!entity.polling.replica_identity_columns?.length) {
        issues.set(entity.name, 'CDC unavailable: Configure a PostgreSQL replica identity for this table or use incremental updates.');
      }
      continue;
    }
    if (selection.sync_mode !== 'incremental') continue;
    if (!canSelectIncremental(entity, selection)) {
      issues.set(entity.name, selection.incremental_behavior === 'append_only'
        ? 'Incomplete settings: Choose Configure to select a timestamp column, then choose Done, or use Full snapshot.'
        : 'Incomplete settings: Choose Configure to select a supported row identifier and timestamp column, then choose Done, or use Full snapshot.');
    } else if (selection.incremental_behavior !== undefined
        && selection.incremental_behavior !== 'reconcile'
        && selection.incremental_behavior !== 'append_only') {
      issues.set(entity.name, 'Incremental behavior must be Reconcile or Append-only.');
    } else if (selection.retention_days !== undefined
        && (selection.incremental_behavior !== 'append_only'
          || !Number.isInteger(selection.retention_days)
          || selection.retention_days < 1 || selection.retention_days > 36500)) {
      issues.set(entity.name, 'Retention requires Append-only and a whole number from 1 to 36500 days.');
    } else if (selection.source_contract_acknowledged !== true) {
      issues.set(entity.name, 'Confirmation needed: Review settings and choose Done to confirm.');
    }
  }
  return issues;
}

/** Validate the entire page selection before the explicit save action. */
export function prepareEntitySelectionSave(
  selected: Map<string, SelectedEntity>, entities: EntityDiscoveryItem[],
): SelectedEntity[] {
  const pending = new Map(selected);
  if (pending.size === 0) throw new Error('Select at least one table before saving.');
  const issues = entitySelectionIssues(pending, entities);
  if (issues.size) {
    const labels = new Map(entities.map(entity => [entity.name, entity.label]));
    throw new Error(Array.from(issues, ([name, message]) =>
      `${labels.get(name) || pending.get(name)?.label || name}: ${message}`).join('\n'));
  }
  return Array.from(pending.values());
}

export type PollingModeChoice = 'full' | 'incremental_reconcile' | 'incremental_append_only';

/** Present the wire-level incremental behavior as one of the three user-facing polling modes. */
export function pollingModeChoice(selection: SelectedEntity): PollingModeChoice {
  if (selection.sync_mode !== 'incremental') return 'full';
  return selection.incremental_behavior === 'append_only'
    ? 'incremental_append_only' : 'incremental_reconcile';
}

/** Incomplete incremental choices are drafts; only eligible mode actions acknowledge the exact tuple. */
export function selectPollingMode(entity: EntityDiscoveryItem, selection: SelectedEntity, mode: string): SelectedEntity {
  if (mode === 'cdc') {
    const identity = entity.polling?.replica_identity_columns ?? [];
    return { ...selection, sync_mode: 'cdc', identity_key_columns: [...identity],
      incremental_field: undefined, source_contract_acknowledged: false,
      incremental_behavior: undefined, retention_days: undefined };
  }
  const isIncremental = mode === 'incremental'
    || mode === 'incremental_reconcile'
    || mode === 'incremental_append_only';
  if (!isIncremental) {
    return { ...selection, sync_mode: 'full', source_contract_acknowledged: false,
      incremental_behavior: undefined, retention_days: undefined };
  }
  const behavior: NonNullable<SelectedEntity['incremental_behavior']> = mode === 'incremental_append_only' ? 'append_only'
    : mode === 'incremental_reconcile' ? 'reconcile'
      : selection.incremental_behavior ?? 'reconcile';
  const behaviorSettings: Pick<SelectedEntity, 'incremental_behavior' | 'retention_days'> = behavior === 'append_only'
    ? { incremental_behavior: behavior, retention_days: selection.retention_days ?? 180 }
    : { incremental_behavior: behavior, retention_days: undefined };
  const candidate = { ...selection, sync_mode: 'incremental' as const, ...behaviorSettings };
  if (!canSelectIncremental(entity, candidate)) {
    return { ...candidate,
      source_contract_acknowledged: false };
  }
  return { ...candidate, source_contract_acknowledged: true,
    identity_key_columns: [...selectedPollingIdentity(entity, candidate)],
    incremental_field: selection.incremental_field,
  };
}

export function selectPollingIdentity(selection: SelectedEntity, columns: string[]): SelectedEntity {
  return { ...selection, identity_key_columns: [...columns],
    source_contract_acknowledged: false };
}

/** Field edits preserve the intended mode; Done confirms the final supported tuple. */
export function selectPollingWatermark(selection: SelectedEntity, field: string): SelectedEntity {
  return { ...selection, incremental_field: field || undefined,
    source_contract_acknowledged: false };
}

/** Confirm only valid dialog settings; never downgrade an incomplete incremental draft. */
export function confirmPollingDraft(entity: EntityDiscoveryItem, selection: SelectedEntity): SelectedEntity | null {
  if (selection.sync_mode === 'incremental' && !canSelectIncremental(entity, selection)) return null;
  return selectPollingMode(entity, selection, selection.sync_mode);
}